import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDashboardStore } from '@store'
import {
	StatCard,
	AlertCard,
	ActivityFeed,
	QuickActions
} from '@components/features/dashboard/widgets'
import {
	LineChart,
	DonutChart
} from '@components/features/dashboard/charts'
import {
	DashboardGrid,
	WidgetContainer,
	StatsRow
} from '@components/features/dashboard/layouts'
import './Dashboard.scss'

/**
 * Super Admin Dashboard Page
 *
 * Displays system-wide metrics, company growth, package distribution,
 * and critical alerts for platform administrators.
 */
const Dashboard = () => {
	const navigate = useNavigate()

	// Dashboard store
	const {
		stats,
		companyGrowth,
		alerts,
		companiesOverview,
		packageDistribution,
		loading,
		error,
		autoRefreshEnabled,
		fetchSuperAdminDashboard,
		fetchPackageDistribution,
		startAutoRefresh,
		stopAutoRefresh,
		getTimeSinceUpdate
	} = useDashboardStore()

	// Fetch dashboard data on mount
	useEffect(() => {
		fetchSuperAdminDashboard()
		fetchPackageDistribution()

		// Start auto-refresh every 60 seconds
		const cleanup = startAutoRefresh('super-admin', 60000)

		return () => {
			cleanup()
			stopAutoRefresh()
		}
	}, [fetchSuperAdminDashboard, fetchPackageDistribution, startAutoRefresh, stopAutoRefresh])

	// Manual refresh handler
	const handleRefresh = useCallback(() => {
		fetchSuperAdminDashboard()
		fetchPackageDistribution()
	}, [fetchSuperAdminDashboard, fetchPackageDistribution])

	// Quick action handlers with improved styling
	const quickActions = [
		{
			icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"/></svg>,
			label: 'Add Company',
			description: 'Create a new company account',
			onClick: () => navigate('/super-admin/companies/new'),
			variant: 'primary'
		},
		{
			icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
			label: 'Manage Packages',
			description: 'Configure subscription packages',
			onClick: () => navigate('/super-admin/packages'),
			variant: 'info'
		},
		{
			icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
			label: 'View Users',
			description: 'Manage platform users',
			onClick: () => navigate('/super-admin/users'),
			variant: 'success'
		},
		{
			icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>,
			label: 'View Reports',
			description: 'Analytics and insights',
			onClick: () => navigate('/super-admin/reports'),
			variant: 'warning'
		},
		{
			icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
			label: 'Settings',
			description: 'Platform configuration',
			onClick: () => navigate('/super-admin/settings')
		}
	]

	// Format company growth data for chart
	// companyGrowth is already the data array from store (extracted from response.data)
	const growthChartData = companyGrowth || []
	const growthChartLines = [
		{ dataKey: 'new_companies', name: 'New Companies', color: 'var(--color-primary)' },
		{ dataKey: 'total_companies', name: 'Total Companies', color: 'var(--color-success)' }
	]

	// Format package distribution for donut chart
	const packageChartData = packageDistribution?.packages || []

	// Format alerts for AlertCard
	const formattedAlerts = alerts.map(alert => ({
		id: alert.id,
		title: alert.title,
		description: alert.description,
		type: alert.priority === 'high' ? 'danger' : alert.priority === 'medium' ? 'warning' : 'info',
		action: alert.link ? () => navigate(alert.link) : null,
		actionLabel: 'View'
	}))

	// Format activity data with proper company names
	const activityData = companiesOverview.slice(0, 5).map(company => ({
		id: company.id,
		user_name: company.name,
		user_initials: company.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'CO',
		action: company.status === 'active' ? 'Active Company' : `Status: ${company.status}`,
		target: `${company.projects_count || 0} projects`,
		time: company.subscription_ends ? `Expires: ${company.subscription_ends}` : 'Subscription Active',
		type: 'company'
	}))

	// Get status color class
	const getStatusClass = (status) => {
		switch (status?.toLowerCase()) {
			case 'active': return 'status--active'
			case 'pending': return 'status--pending'
			case 'suspended': return 'status--suspended'
			case 'expired': return 'status--expired'
			default: return 'status--default'
		}
	}

	return (
		<div className="dashboard-page super-admin-dashboard">
			{/* Enhanced Page Header */}
			<div className="dashboard-header">
				<div className="dashboard-header__left">
					<div className="dashboard-header__greeting">
						<h1 className="dashboard-header__title">Dashboard</h1>
						<p className="dashboard-header__subtitle">Welcome back! Here's what's happening with your platform.</p>
					</div>
				</div>
				<div className="dashboard-header__right">
					<div className="dashboard-header__status">
						<span className="dashboard-header__time">
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/>
							</svg>
							{getTimeSinceUpdate()}
						</span>
						{autoRefreshEnabled && (
							<span className="dashboard-header__auto-badge">
								<span className="pulse-dot"></span>
								Live
							</span>
						)}
					</div>
					<button
						className="dashboard-header__refresh-btn"
						onClick={handleRefresh}
						disabled={loading}
					>
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={loading ? 'spinning' : ''}>
							<path d="M23 4v6h-6M1 20v-6h6"/>
							<path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
						</svg>
						{loading ? 'Refreshing...' : 'Refresh'}
					</button>
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className="dashboard-alert dashboard-alert--error">
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
					</svg>
					<p>{error}</p>
					<button onClick={handleRefresh} className="dashboard-alert__retry">Retry</button>
				</div>
			)}

			{/* Stats Row */}
			<StatsRow>
				<StatCard
					title="Total Companies"
					value={stats?.total_companies ?? '-'}
					subtitle={stats?.companies_growth > 0 ? `+${stats.companies_growth}% this month` : 'All registered companies'}
					trend={stats?.companies_growth > 0 ? `+${stats.companies_growth}%` : null}
					trendUp={stats?.companies_growth > 0}
					icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"/></svg>}
					variant="primary"
					loading={loading}
					onClick={() => navigate('/super-admin/companies')}
				/>
				<StatCard
					title="Active Companies"
					value={stats?.active_companies ?? '-'}
					subtitle={stats?.pending_companies > 0 ? `${stats.pending_companies} pending activation` : 'All companies active'}
					icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>}
					variant="success"
					loading={loading}
					onClick={() => navigate('/super-admin/companies?status=active')}
				/>
				<StatCard
					title="Monthly Revenue"
					value={stats?.monthly_revenue ? `₹${stats.monthly_revenue.toLocaleString('en-IN')}` : '-'}
					subtitle={stats?.revenue_growth > 0 ? `+${stats.revenue_growth}% vs last month` : 'Current month earnings'}
					trend={stats?.revenue_growth > 0 ? `+${stats.revenue_growth}%` : null}
					trendUp={stats?.revenue_growth > 0}
					icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><text x="6" y="17" fontSize="14" fill="currentColor" stroke="none" fontWeight="bold">₹</text></svg>}
					variant="info"
					loading={loading}
				/>
				<StatCard
					title="Active Projects"
					value={stats?.total_projects ?? '-'}
					subtitle="Across all companies"
					icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>}
					variant="warning"
					loading={loading}
				/>
			</StatsRow>

			{/* Charts Row */}
			<DashboardGrid columns={2}>
				{/* Company Growth Chart */}
				<WidgetContainer
					title="Company Growth"
					subtitle="Monthly registration trends"
					onAction={handleRefresh}
					actionLabel="Refresh"
					loading={loading}
				>
					{growthChartData.length > 0 ? (
						<LineChart
							data={growthChartData}
							lines={growthChartLines}
							xAxisKey="month"
							height={280}
							showGrid={true}
							showLegend={true}
							loading={loading}
						/>
					) : (
						<div className="empty-chart-state">
							<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
								<path d="M3 3v18h18"/><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
							</svg>
							<h4>No Growth Data Yet</h4>
							<p>Company growth trends will appear here once data is available.</p>
						</div>
					)}
				</WidgetContainer>

				{/* Package Distribution */}
				<WidgetContainer
					title="Package Distribution"
					subtitle="Companies by subscription tier"
					onAction={() => navigate('/super-admin/packages')}
					actionLabel="Manage Packages"
					loading={loading}
				>
					{packageChartData.length > 0 ? (
						<DonutChart
							data={packageChartData}
							dataKey="count"
							nameKey="name"
							height={280}
							showLegend={true}
							centerText={packageDistribution?.total?.toString() || '0'}
							loading={loading}
						/>
					) : (
						<div className="empty-chart-state">
							<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
								<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
							</svg>
							<h4>No Package Data</h4>
							<p>Package distribution will appear once companies are assigned packages.</p>
						</div>
					)}
				</WidgetContainer>
			</DashboardGrid>

			{/* Alerts and Activity Row */}
			<DashboardGrid columns={2}>
				{/* High Priority Alerts */}
				<WidgetContainer
					title="System Alerts"
					subtitle="Items requiring attention"
					loading={loading}
				>
					{formattedAlerts.length > 0 ? (
						<AlertCard
							items={formattedAlerts}
							type="warning"
							loading={loading}
						/>
					) : (
						<div className="empty-state-card">
							<div className="empty-state-card__icon empty-state-card__icon--success">
								<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
								</svg>
							</div>
							<h4>All Clear!</h4>
							<p>No critical alerts at this time. Your platform is running smoothly.</p>
						</div>
					)}
				</WidgetContainer>

				{/* Recent Companies */}
				<WidgetContainer
					title="Recent Companies"
					subtitle="Latest registered companies"
					onAction={() => navigate('/super-admin/companies')}
					actionLabel="View All"
					loading={loading}
				>
					{activityData.length > 0 ? (
						<div className="recent-companies-list">
							{companiesOverview.slice(0, 5).map(company => (
								<div
									key={company.id}
									className="recent-company-item"
									onClick={() => navigate(`/super-admin/companies/${company.id}`)}
								>
									<div className="recent-company-item__avatar">
										{company.name?.charAt(0).toUpperCase() || 'C'}
									</div>
									<div className="recent-company-item__info">
										<span className="recent-company-item__name">{company.name}</span>
										<span className="recent-company-item__meta">
											{company.package_name || 'No Package'} • {company.projects_count || 0} projects
										</span>
									</div>
									<span className={`recent-company-item__status ${getStatusClass(company.status)}`}>
										{company.status}
									</span>
								</div>
							))}
						</div>
					) : (
						<div className="empty-state-card">
							<div className="empty-state-card__icon">
								<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/>
								</svg>
							</div>
							<h4>No Companies Yet</h4>
							<p>Companies will appear here once they're registered.</p>
						</div>
					)}
				</WidgetContainer>
			</DashboardGrid>

			{/* Companies Overview Table */}
			<WidgetContainer
				title="Companies Overview"
				subtitle="All companies at a glance"
				onAction={() => navigate('/super-admin/companies')}
				actionLabel="View All Companies"
				loading={loading}
				fullWidth
			>
				<div className="companies-table-wrapper">
					<table className="companies-table">
						<thead>
							<tr>
								<th>Company</th>
								<th>Package</th>
								<th>Projects</th>
								<th>Progress</th>
								<th>Subscription</th>
								<th>Status</th>
							</tr>
						</thead>
						<tbody>
							{loading ? (
								<tr>
									<td colSpan="6" className="table-loading-state">
										<div className="loading-spinner"></div>
										<span>Loading companies...</span>
									</td>
								</tr>
							) : companiesOverview.length === 0 ? (
								<tr>
									<td colSpan="6" className="table-empty-state">
										<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
											<path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/>
										</svg>
										<span>No companies found</span>
									</td>
								</tr>
							) : (
								companiesOverview.slice(0, 5).map(company => (
									<tr
										key={company.id}
										onClick={() => navigate(`/super-admin/companies/${company.id}`)}
										className="table-row-clickable"
									>
										<td>
											<div className="company-cell">
												<div className="company-cell__avatar">
													{company.name?.charAt(0).toUpperCase() || 'C'}
												</div>
												<span className="company-cell__name">{company.name}</span>
											</div>
										</td>
										<td>
											<span className="package-badge">
												{company.package_name || 'N/A'}
											</span>
										</td>
										<td className="projects-cell">
											<span className="projects-count">{company.projects_count || 0}</span>
										</td>
										<td>
											<div className="progress-cell">
												<div className="progress-bar-mini">
													<div
														className="progress-bar-mini__fill"
														style={{ width: `${company.avg_progress || 0}%` }}
													/>
												</div>
												<span className="progress-value">{company.avg_progress || 0}%</span>
											</div>
										</td>
										<td>
											{company.days_remaining != null ? (
												<span className={`subscription-days ${company.days_remaining < 30 ? 'subscription-days--warning' : company.days_remaining < 7 ? 'subscription-days--critical' : ''}`}>
													{company.days_remaining} days
												</span>
											) : (
												<span className="subscription-days subscription-days--na">N/A</span>
											)}
										</td>
										<td>
											<span className={`status-pill ${getStatusClass(company.status)}`}>
												{company.status}
											</span>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</WidgetContainer>

			{/* Quick Actions */}
			<div className="quick-actions-section">
				<QuickActions
					actions={quickActions}
					title="Quick Actions"
					columns={5}
				/>
			</div>
		</div>
	)
}

export default Dashboard
